import { GuideTopic } from "../GuideBook";
import {
  CardsScene,
  CardsFilterScene,
  CardMenuScene,
  FolderScene,
  ProductionScene,
  ReportScene,
  ResetPasswordScene,
  RolesScene,
  StatusScene,
  TableFilterScene,
  TableScene,
  UserFormScene,
} from "../art/scenes";
import { GoalScene } from "views/components/LabelPropagation/guides/art/ReviewScenes";

export const INVENTORY_TOPICS: GuideTopic[] = [
  {
    key: "cards",
    label: "Dataset cards",
    title: "Your datasets at a glance",
    body: (
      <>
        <p>
          The Dataset Inventory is your home page and the start of every
          workflow. Each card shows:
        </p>
        <ul>
          <li>
            <strong>Name</strong>: hover and click to rename it inline.
          </li>
          <li>
            <strong>Creation date</strong> and the <strong>status badge</strong>
            .
          </li>
          <li>
            <strong>Counts</strong> of images, objects, videos and frames. They
            show ".." until the dataset is Ready.
          </li>
          <li>
            <strong>Info icon</strong>: hover for the Job Name, Setup Name and
            Recipe Names that all folders in the dataset share.
          </li>
        </ul>
        <p>
          Click a Ready card to explore it. A card that is still being created
          opens the creation progress instead.
        </p>
      </>
    ),
    art: <CardsScene />,
  },
  {
    key: "status",
    label: "Status badges",
    title: "What each badge means",
    body: (
      <>
        <ul>
          <li>
            <strong>Draft</strong>: files uploaded, pipeline not started.
            Limited actions.
          </li>
          <li>
            <strong>Indexing</strong>: creation or Re-Index is running. Wait for
            it to finish.
          </li>
          <li>
            <strong>Ready</strong>: stable and fully usable.
          </li>
          <li>
            <strong>Read Only</strong>: a data-changing task is running (Label
            Propagation, media addition, snapshot). Explore only.
          </li>
          <li>
            <strong>Partial Index</strong>: new media was added but not
            re-indexed. Add more media or Re-Index.
          </li>
          <li>
            <strong>Error</strong>: the last task failed. Use Recover or Delete
            from the card menu.
          </li>
        </ul>
      </>
    ),
    tip: "Typical flows: Draft to Indexing to Ready. Media addition: Ready to Read Only to Partial Index, then Re-Index back to Ready.",
    art: <StatusScene />,
  },
  {
    key: "filters",
    label: "Filters and sorting",
    title: "Find a dataset quickly",
    body: (
      <>
        <ul>
          <li>
            <strong>Search bar</strong>: locate datasets by name.
          </li>
          <li>
            <strong>Ownership</strong>: All, Owned by me, or Shared with me.
          </li>
          <li>
            <strong>Status</strong>: Ready, Draft, Error, Indexing, Read Only or
            Partial Index.
          </li>
          <li>
            <strong>Job, Setup, Recipe</strong>: the production metadata read
            from the AOI folders.
          </li>
          <li>
            <strong>Sort by</strong> status, date created, size or name.
          </li>
        </ul>
        <p>
          The counter shows how many datasets match. Use{" "}
          <strong>Clear Filters</strong> to reset.
        </p>
      </>
    ),
    art: <CardsFilterScene />,
  },
  {
    key: "create",
    label: "New dataset",
    title: "Create a dataset from AOI folders",
    body: (
      <>
        <p>
          Click <strong>New Dataset</strong> and choose where the data comes
          from:
        </p>
        <ul>
          <li>
            <strong>Import from Scan Result</strong>: browse the Camtek
            DataServer by Job, Setup, Lot or date, tick wafers from one scan
            result, and Visual Layer exports them into folders for you.
            Available only when DataServer is configured and reachable.
          </li>
          <li>
            <strong>Upload folder</strong>: enter only the folder name. The base
            path is configured during installation.
          </li>
          <li>
            Each folder must contain <strong>DB.csv</strong>,{" "}
            <strong>metadata.json</strong>, <strong>classes.json</strong> and an{" "}
            <strong>images</strong> folder.
          </li>
          <li>
            After you select the first folder, folders with a different Job,
            Setup, Recipe or class list are greyed out. Hover them to see why.
          </li>
          <li>
            Preview a few images, then click <strong>Create Dataset</strong>.
            Progress runs through Uploading, Initializing, Pre-Processing,
            Indexing and Saving.
          </li>
        </ul>
      </>
    ),
    tip: "classes.json becomes the dataset's fixed class list. Labels cannot be added or removed later, so include every defect type now. Labels in the OClass column of DB.csv are imported as User Labels.",
    art: <FolderScene />,
  },
  {
    key: "menu",
    label: "Card menu",
    title: "Delete, duplicate and recover",
    body: (
      <>
        <ul>
          <li>
            <strong>Copy URL</strong>: share a direct link to the dataset.
          </li>
          <li>
            <strong>Delete</strong>: always available except while Indexing.
            Needs the Owner role.
          </li>
          <li>
            <strong>Recover</strong>: appears on datasets in Error. It restores
            the automatic snapshot taken before the failed task. Needs the Owner
            or Admin role.
          </li>
        </ul>
        <p>
          Recovery works for failures after the dataset was first created (Label
          Propagation, media addition, Re-Index). If creation itself failed
          there is nothing to restore, so the dataset can only be deleted.
        </p>
      </>
    ),
    art: <CardMenuScene />,
  },
];

export const TASK_MANAGER_TOPICS: GuideTopic[] = [
  {
    key: "overview",
    label: "Overview",
    title: "Every operation in one place",
    body: (
      <>
        <p>
          The Task Manager lists all operations from the last 14 days across the
          workspace and refreshes automatically. Use it to monitor active work,
          review history and troubleshoot failures.
        </p>
        <ul>
          <li>Click a dataset name to open it.</li>
          <li>Click any column header to sort.</li>
          <li>
            The row menu offers <strong>Copy Task ID</strong> for support
            requests.
          </li>
        </ul>
      </>
    ),
    tip: "If nothing ran in the last 14 days the table is empty.",
    art: <TableScene />,
  },
  {
    key: "types",
    label: "Task types",
    title: "What can appear here",
    body: (
      <ul>
        <li>
          <strong>Dataset Creation</strong>: building a new dataset.
        </li>
        <li>
          <strong>Media Addition</strong>: adding folders to an existing
          dataset.
        </li>
        <li>
          <strong>Reindex</strong>: integrating added media into the clusters.
        </li>
        <li>
          <strong>Label Propagation</strong>: one continuous task that
          alternates between Running and Waiting for review.
        </li>
        <li>
          <strong>Training</strong> and <strong>Model Validation</strong>: jobs
          sent to the Camtek infrastructure.
        </li>
        <li>
          <strong>Snapshot Restore</strong> and <strong>Snapshot Clone</strong>.
        </li>
      </ul>
    ),
    art: <TableScene />,
  },
  {
    key: "status",
    label: "Statuses",
    title: "Reading the Status column",
    body: (
      <ul>
        <li>
          <strong>Pending</strong>: queued until a resource is free. Duration
          does not count yet.
        </li>
        <li>
          <strong>Initializing</strong>: validating and preparing data.
        </li>
        <li>
          <strong>Running</strong>: in progress, with a percentage.
        </li>
        <li>
          <strong>Waiting for review</strong>: a Label Propagation batch is
          ready. Open the dataset to review it.
        </li>
        <li>
          <strong>Completed</strong>, <strong>Aborted</strong> (stopped by a
          user) or <strong>Failed</strong>: hover a failed status to read the
          error.
        </li>
      </ul>
    ),
    tip: "Abort is available only for Dataset Creation tasks that are pending, initializing or running.",
    art: <TableScene />,
  },
  {
    key: "filters",
    label: "Filters",
    title: "Narrow the list",
    body: (
      <>
        <p>
          Filter by time range (last 24 hours, 7 days or 14 days), task type,
          dataset, dataset ID, Job, Setup, Recipe List, status, or the user who
          started the task. Filters are kept in the URL, so you can share a
          filtered view.
        </p>
        <p>
          Click <strong>Reset</strong> to return to the default view.
        </p>
      </>
    ),
    art: <TableFilterScene />,
  },
  {
    key: "concurrency",
    label: "Concurrency",
    title: "Which tasks block each other",
    body: (
      <>
        <ul>
          <li>
            <strong>Ready</strong>: every task is allowed.
          </li>
          <li>
            <strong>Read Only</strong>: search, filter, share and tags work.
            Media addition, Re-Index, Label Propagation and snapshots wait.
          </li>
          <li>
            <strong>Partial Index</strong>: add media or Re-Index only.
          </li>
        </ul>
        <p>
          Training and validation never block other work. Up to 4 Label
          Propagation tasks can run in parallel; GPU tasks such as dataset
          creation run one at a time.
        </p>
      </>
    ),
    art: <StatusScene />,
  },
];

export const MODEL_CATALOG_TOPICS: GuideTopic[] = [
  {
    key: "tabs",
    label: "Overview",
    title: "Where completed jobs live",
    body: (
      <>
        <p>
          The Model Catalog opens on <strong>Training Jobs</strong>; switch to{" "}
          <strong>Validation Jobs</strong> at the top. Only successful jobs are
          listed. Failed jobs stay in the Task Manager.
        </p>
        <ul>
          <li>
            Columns: model name, type (Classification), source (Camtek Auto ML),
            dataset, Job, Setup, Recipe List, Tool List, last updated.
          </li>
          <li>
            Validation jobs add <strong>GT Source</strong>: VL for propagated
            labels, USER for imported labels.
          </li>
        </ul>
      </>
    ),
    art: <TableScene />,
  },
  {
    key: "results",
    label: "Reports and export",
    title: "Open or download a report",
    body: (
      <>
        <ul>
          <li>
            <strong>Results</strong> opens the HTML report inside Visual Layer.
          </li>
          <li>
            The row menu <strong>Export</strong> downloads the whole results
            folder as a ZIP: validation_summary.html, report.html,
            predictions.csv, confusion_matrix.csv, logits.csv and the debug
            images.
          </li>
        </ul>
        <p>
          The Validation Summary shows a confusion matrix with precision,
          recall, F1 and FP rate per class. The Sample Metrics report lists
          per-image confidence (margin, entropy, energy, ensemble disagreement).
        </p>
      </>
    ),
    tip: "Reports work offline. Keep the extracted folder for audits.",
    art: <ReportScene />,
  },
  {
    key: "filters",
    label: "Filters",
    title: "Find a model",
    body: (
      <p>
        Use the dropdowns above the table to filter by model name, dataset, Job,
        Setup, Recipe or tool. Each dropdown has its own Reset; the filter bar
        Reset clears all of them. Every column sorts ascending or descending.
      </p>
    ),
    art: <TableFilterScene />,
  },
  {
    key: "production",
    label: "Production flag",
    title: "Mark a model as production",
    body: (
      <p>
        The <strong>Production</strong> checkbox marks a model as the one to
        deploy. Only the workspace Admin can change it, and unmarking asks for
        confirmation. Admins can also delete a model from the row menu.
      </p>
    ),
    art: <ProductionScene />,
  },
];

export const WORKSPACE_SETTINGS_TOPICS: GuideTopic[] = [
  {
    key: "roles",
    label: "Roles",
    title: "Workspace and dataset roles",
    body: (
      <>
        <ul>
          <li>
            <strong>Admin</strong>: one per workspace. Manages users, the
            Train-Worthy threshold and production models. Is an Owner of every
            dataset.
          </li>
          <li>
            <strong>Editor</strong>: creates datasets, adds media, runs Label
            Propagation, snapshots and Model Jobs.
          </li>
          <li>
            <strong>Viewer</strong>: explores and exports only. Cannot be
            promoted to Editor on a dataset.
          </li>
        </ul>
        <p>
          Dataset roles are separate: the creator and the Admin are Owners, and
          other members start as Viewers until someone shares the dataset with
          them as Editor.
        </p>
      </>
    ),
    tip: "Promoting another user to Admin demotes you to Editor and logs you out, since there is a single Admin.",
    art: <RolesScene />,
  },
  {
    key: "create",
    label: "Create a user",
    title: "Add a team member",
    body: (
      <>
        <p>
          Click <strong>Create User</strong> and fill in:
        </p>
        <ul>
          <li>
            <strong>Username</strong> (no spaces), first and last name.
          </li>
          <li>
            <strong>Role</strong>: Editor or Viewer.
          </li>
          <li>
            <strong>Password</strong>: at least 8 characters. Share it with the
            user as their temporary password.
          </li>
        </ul>
        <p>
          The user is created immediately; there is no email invitation. On
          first login they enter the temporary password on the Get Started
          screen and are asked to set a new one.
        </p>
      </>
    ),
    art: <UserFormScene />,
  },
  {
    key: "password",
    label: "Reset a password",
    title: "When someone forgets their password",
    body: (
      <>
        <p>
          Users cannot reset their own password. The login screen tells them to
          contact their Admin.
        </p>
        <ol>
          <li>
            Open the row menu for the user and choose{" "}
            <strong>Reset Password</strong>.
          </li>
          <li>Set a temporary password and pass it to the user.</li>
          <li>
            The user logs in with it and is prompted to choose a new password
            (at least 8 characters) before continuing.
          </li>
        </ol>
      </>
    ),
    art: <ResetPasswordScene />,
  },
  {
    key: "manage",
    label: "Change role or delete",
    title: "Keep roles up to date",
    body: (
      <>
        <ul>
          <li>
            <strong>Change Role</strong> from the inline select. You cannot
            change your own role.
          </li>
          <li>
            Demoting a user to Viewer also lowers their access on every dataset.
          </li>
          <li>
            <strong>Delete User</strong> removes the account. Their datasets
            transfer to the Admin. You cannot delete yourself.
          </li>
        </ul>
        <p>
          Non-admin members see a read-only <strong>Workspace Members</strong>{" "}
          table instead of these controls.
        </p>
      </>
    ),
    art: <RolesScene />,
  },
  {
    key: "threshold",
    label: "Train-Worthy threshold",
    title: "How many labeled images are enough",
    body: (
      <>
        <p>
          The threshold is the minimum number of labeled images{" "}
          <strong>per label</strong> before a dataset counts as Train-Worthy and
          can be sent to training. The default is 200.
        </p>
        <ul>
          <li>
            Enter a value in <strong>System settings</strong> and click{" "}
            <strong>Save</strong>. It applies to every dataset.
          </li>
          <li>
            The Train-Worthy Goal card in Label Propagation tracks progress
            toward it.
          </li>
          <li>
            Reaching it does not end Label Propagation; you can finish early
            once it is met.
          </li>
        </ul>
      </>
    ),
    art: <GoalScene />,
  },
];
